/**
 * Pipeline action slice — Phase 8 commit 81.
 *
 * Extracted from `store.ts` so that the actions which initiate or
 * interact with backend coagent endpoints (research / brand / growth /
 * geo / ecommerce / content-intelligence / social-video, plus the
 * product-metric mutators that fan out fire-and-forget POSTs) are kept
 * separate from the message / step / hydration / persistence concerns.
 *
 * The slice receives Zustand's `set` and `get` from the store creator
 * and returns the corresponding subset of `CoAgentState`. Module-level
 * `_researchAbortController` lives here so all starters share the same
 * cancellation handle.
 */

/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { StoreApi } from "zustand";
import { env } from "@/config/env";
import type {
  CoAgentState,
  CoAgentType,
  BrandInput,
  SocialBrandInput,
  BrandCoAgentInput,
  GrowthPipelineInput,
  GeoPipelineInput,
  EcommercePipelineInput,
} from "../types";
import {
  authHeaders,
  normalizeWebsite,
  getMaxStepIndexForType,
  clearActiveStepStatuses,
  recalcDerivedMetrics,
} from "../utils";
import { runResearchSSE } from "../pipelines/researchSSE";
import { runSocialResearchSSE } from "../pipelines/socialSSE";
import { runBrandResearchSSE } from "../pipelines/brandSSE";
import { runGrowthResearchSSE } from "../pipelines/growthSSE";
import { runGeoResearchSSE } from "../pipelines/geoSSE";
import { runEcommerceResearchSSE } from "../pipelines/ecommerceSSE";
import { runEcommerceMetricsSSE } from "../pipelines/ecommerceMetricsSSE";
import { runSocialVideoGenerateSSE } from "../pipelines/socialVideoSSE";
import {
  runContentIntelligenceSSE,
  type ContentIntelligenceInput,
} from "../pipelines/contentIntelligenceSSE";
import { CONTENT_INSPIRATION_STEP_INDEX } from "../pipelines/ecommerceHighlights";
import { trackBrandPipelineStarted, trackBrandStepCompleted } from "@/shared/lib/gtm";

type SetState = StoreApi<CoAgentState>["setState"];
type GetState = StoreApi<CoAgentState>["getState"];

let _researchAbortController: AbortController | null = null;

/**
 * Aborts any in-flight pipeline started by this slice. Called by the
 * store's `reset` action so pipelines don't keep mutating after the
 * session is torn down.
 */
export function abortPendingPipelines(): void {
  _researchAbortController?.abort();
  _researchAbortController = null;
}

export type PipelineActions = Pick<
  CoAgentState,
  | "startResearch"
  | "startSocialResearch"
  | "startBrandResearch"
  | "confirmBrandStrategy"
  | "startGrowthResearch"
  | "continueGrowthResearch"
  | "startGeoResearch"
  | "startEcommerceResearch"
  | "startEcommerceMetrics"
  | "startContentIntelligence"
  | "approveContentBrief"
  | "updateProductMetric"
  | "bulkUpdateProductMetric"
  | "startSocialVideoGeneration"
  | "generateSocialVideoPreview"
  | "sendBriefToProduction"
>;

export function createPipelineActions(set: SetState, get: GetState): PipelineActions {
  return {
    startResearch: async (brandInput: BrandInput) => {
      const { sessionId, addStepLog, addMessage, updateCampaignData } = get();
      if (!sessionId) return;

      updateCampaignData({
        brandInput,
        researchStatus: "running",
        researchPhase: "prompt_generation",
      });
      set((state) => ({
        currentStepIndex: 1,
        stepStatuses: { ...state.stepStatuses, 0: "completed", 1: "running" },
      }));

      addMessage({
        role: "agent",
        content: `Starting multi-engine research for **${brandInput.brandName}** in the **${brandInput.industry}** space...`,
        widget: "researchProgress",
      });

      _researchAbortController?.abort();
      const controller = new AbortController();
      _researchAbortController = controller;

      try {
        await runResearchSSE(brandInput, sessionId, controller.signal);
      } catch (e: any) {
        if (e.name === "AbortError") return;
        updateCampaignData({ researchStatus: "error", researchPhase: "error" });
        addStepLog(1, `[ERROR] ${e.message}`);
        set((state) => ({
          currentStepIndex: 0,
          stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
        }));
        addMessage({
          role: "agent",
          content: `Research failed: ${e.message}. Please try again.`,
        });
      } finally {
        if (_researchAbortController === controller) {
          _researchAbortController = null;
        }
      }
    },

    startSocialResearch: async (brandInput: SocialBrandInput, brandId?: string) => {
      const { sessionId, addStepLog, addMessage, updateCampaignData } = get();
      if (!sessionId) return;

      updateCampaignData({
        socialBrandInput: brandInput,
        socialResearchStatus: "running",
        socialResearchPhase: "query_plan_generation",
      });
      set((state) => ({
        coAgentType: "content" as CoAgentType,
        currentStepIndex: 1,
        stepStatuses: { ...state.stepStatuses, 0: "completed", 1: "running" },
      }));

      addMessage({
        role: "agent",
        content: `Starting social media research for **${brandInput.brandName}** across **${brandInput.platforms.join(", ")}**...`,
        widget: "socialProgress",
      });

      _researchAbortController?.abort();
      const controller = new AbortController();
      _researchAbortController = controller;

      try {
        await runSocialResearchSSE(brandInput, sessionId, controller.signal, brandId);
      } catch (e: any) {
        if (e.name === "AbortError") return;
        updateCampaignData({ socialResearchStatus: "error", socialResearchPhase: "error" });
        addStepLog(1, `[ERROR] ${e.message}`);
        set((state) => ({
          currentStepIndex: 0,
          stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
        }));
        addMessage({
          role: "agent",
          content: `Social research failed: ${e.message}. Please try again.`,
        });
      } finally {
        if (_researchAbortController === controller) {
          _researchAbortController = null;
        }
      }
    },

    startBrandResearch: async (
      input: BrandCoAgentInput,
      options?: { mode?: "create" | "refresh"; targetBrandId?: string },
    ) => {
      const { sessionId, addStepLog, addMessage, updateCampaignData } = get();
      const mode: "create" | "refresh" = options?.mode ?? "create";
      const isRefresh = mode === "refresh";

      console.warn(
        "[companion:store] startBrandResearch sessionId=" +
          (sessionId ?? "NULL") +
          " brand=" +
          input?.brandName +
          " mode=" +
          mode +
          (options?.targetBrandId ? " targetBrandId=" + options.targetBrandId : ""),
      );
      if (!sessionId) {
        console.warn("[companion:store] startBrandResearch ABORTED — no sessionId");
        return;
      }

      trackBrandPipelineStarted({
        brandName: input.brandName,
        genre: input.genre,
        hasWebsite: Boolean(input.website),
        competitorCount: input.competitors?.length ?? 0,
      });

      updateCampaignData({
        brandCoAgentInput: input,
        brandResearchStatus: "running",
        brandResearchPhase: input.website ? "website_scrape" : "query_generation",
        brandResearchMode: mode,
        brandResearchTargetBrandId: options?.targetBrandId,
        brandStrategyAwaitingConfirmation: false,
        brandPendingPopulateResult: undefined,
      });
      // Wizard step state only advances on the create flow. Refreshes from
      // the brand-detail page reuse the SSE pipeline but must not touch
      // `currentStepIndex` / `stepStatuses` — those drive the wizard UI.
      if (!isRefresh) {
        set((state) => ({
          coAgentType: "brand-dna" as CoAgentType,
          currentStepIndex: 1,
          stepStatuses: { ...state.stepStatuses, 0: "completed", 1: "running" },
        }));

        addMessage({
          role: "agent",
          content: `Starting brand research for **${input.brandName}** — analyzing trends, market, and content landscape...`,
          widget: "brandResearchProgress",
        });
      }

      _researchAbortController?.abort();
      const controller = new AbortController();
      _researchAbortController = controller;

      try {
        await runBrandResearchSSE(input, sessionId, controller.signal, {
          targetBrandId: options?.targetBrandId,
        });
      } catch (e: any) {
        if (e.name === "AbortError") return;
        updateCampaignData({
          brandResearchStatus: "error",
          brandResearchPhase: "error",
          brandStrategyAwaitingConfirmation: false,
          brandPendingPopulateResult: undefined,
        });
        if (!isRefresh) {
          addStepLog(1, `[ERROR] ${e.message}`);
          set((state) => ({
            currentStepIndex: 0,
            stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
          }));
          addMessage({
            role: "agent",
            content: `Brand research failed: ${e.message}. Please try again.`,
          });
        }
      } finally {
        if (_researchAbortController === controller) {
          _researchAbortController = null;
        }
      }
    },

    confirmBrandStrategy: () => {
      const state = get();
      const pendingPopulateResult = state.campaignData.brandPendingPopulateResult;
      const isAwaitingConfirmation = state.campaignData.brandStrategyAwaitingConfirmation;

      if (!isAwaitingConfirmation) return;
      if (!pendingPopulateResult) {
        state.addStepLog(2, "Brand identity package is still preparing...");
        return;
      }

      trackBrandStepCompleted("brand_strategy", {
        has_populate_result: true,
      });

      state.updateCampaignData({
        brandStrategyAwaitingConfirmation: false,
        brandPendingPopulateResult: undefined,
        brandPopulateResult: pendingPopulateResult,
        brandResearchStatus: "complete",
        brandResearchPhase: "logo_generation",
        brandResearchProgress: { phase: "brand_generation", completed: 1, total: 1 },
      });

      set((prev) => ({
        currentStepIndex: 3,
        focusedStepIndex: 3,
        stepStatuses: { ...prev.stepStatuses, 2: "completed", 3: "waiting" },
      }));

      state.addStepLog(2, "[SUCCESS] Strategy approved");
      state.addStepLog(3, "Brand identity ready for review and logo generation");
    },

    startGrowthResearch: async (input: GrowthPipelineInput) => {
      const { sessionId, addMessage, updateCampaignData } = get();
      if (!sessionId) return;

      const normalizedInput: GrowthPipelineInput = {
        ...input,
        url: normalizeWebsite(input.url),
        businessName: input.businessName.trim(),
        industry: input.industry.trim(),
        valueProposition: input.valueProposition?.trim() || undefined,
        businessModel: input.businessModel?.trim() || undefined,
      };

      updateCampaignData({
        growthInput: normalizedInput,
      });

      set((state) => ({
        coAgentType: "ads" as CoAgentType,
        currentStepIndex: 1,
        stepStatuses: { ...state.stepStatuses, 0: "completed", 1: "running" },
      }));

      addMessage({
        role: "agent",
        content: `Great! I've saved your business details. Let's connect your Google accounts so I can see what tracking you already have and map out the rest of your funnel.`,
        widget: "googleAuth",
      });
    },

    continueGrowthResearch: async () => {
      const { sessionId, addStepLog, addMessage, updateCampaignData } = get();
      const state = get();
      if (!sessionId || !state.campaignData.growthInput) return;

      const normalizedInput = state.campaignData.growthInput;

      updateCampaignData({
        growthResearchStatus: "running",
        growthResearchPhase: "website_scrape",
      });

      set((s) => ({
        currentStepIndex: 2,
        stepStatuses: { ...s.stepStatuses, 1: "completed", 2: "running" },
      }));

      addMessage({
        role: "agent",
        content: `Google accounts connected successfully. Starting deep growth analysis for **${normalizedInput.businessName}** — crawling website and analyzing your existing funnel tracking...`,
        widget: "growthProgress",
      });

      _researchAbortController?.abort();
      const controller = new AbortController();
      _researchAbortController = controller;

      try {
        await runGrowthResearchSSE(
          normalizedInput,
          sessionId,
          controller.signal,
          state.campaignData.googleIds as Record<string, string>,
        );
      } catch (e: any) {
        if (e.name === "AbortError") return;
        updateCampaignData({ growthResearchStatus: "error", growthResearchPhase: "error" });
        addStepLog(2, `[ERROR] ${e.message}`);
        set((s) => ({
          currentStepIndex: 1,
          stepStatuses: { ...s.stepStatuses, 1: "waiting", 2: "pending" },
        }));
        addMessage({
          role: "agent",
          content: `Growth research failed: ${e.message}. Please try again.`,
        });
      } finally {
        if (_researchAbortController === controller) {
          _researchAbortController = null;
        }
      }
    },

    startGeoResearch: async (input: GeoPipelineInput) => {
      const { sessionId, addStepLog, addMessage, updateCampaignData } = get();
      if (!sessionId) return;

      updateCampaignData({
        geoInput: input,
        geoResearchStatus: "running",
        geoResearchPhase: "site_scan",
        geoResearchProgress: { phase: "site_scan", completed: 0, total: 6 },
      });
      set((state) => ({
        coAgentType: "geo" as CoAgentType,
        currentStepIndex: 1,
        stepStatuses: { ...state.stepStatuses, 0: "completed", 1: "running" },
      }));

      addMessage({
        role: "agent",
        content: `Starting GEO audit for **${input.businessName}** — scanning your site and deriving high-intent queries for AI citation checks...`,
        widget: "geoProgress",
      });

      _researchAbortController?.abort();
      const controller = new AbortController();
      _researchAbortController = controller;

      try {
        await runGeoResearchSSE(input, sessionId, controller.signal);
      } catch (e: any) {
        if (e.name === "AbortError") return;
        updateCampaignData({ geoResearchStatus: "error", geoResearchPhase: "error" });
        addStepLog(1, `[ERROR] ${e.message}`);
        set((state) => ({
          currentStepIndex: 0,
          stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
        }));
        addMessage({
          role: "agent",
          content: `GEO research failed: ${e.message}. Please try again.`,
        });
      } finally {
        if (_researchAbortController === controller) {
          _researchAbortController = null;
        }
      }
    },

    startEcommerceResearch: async (input: EcommercePipelineInput) => {
      const { sessionId, addStepLog, updateCampaignData } = get();
      if (!sessionId) return;

      let shopDomain = (input.shopDomain ?? "").trim().toLowerCase();
      if (input.connectionMethod !== "manual" && shopDomain) {
        shopDomain = shopDomain.replace(/^https?:\/\//, "");
        shopDomain = shopDomain.split(/[/?#]/)[0];
        shopDomain = shopDomain.replace(/^www\./, "");
        if (!shopDomain.endsWith(".myshopify.com")) {
          shopDomain = `${shopDomain}.myshopify.com`;
        }
      }

      const normalizedInput: EcommercePipelineInput = {
        ...input,
        shopDomain: shopDomain || undefined,
        businessName: input.businessName.trim(),
        industry: input.industry?.trim() || undefined,
        targetAudience: input.targetAudience?.trim() || undefined,
      };

      updateCampaignData({
        ecommerceInput: normalizedInput,
        ecommerceResearchStatus: "running",
        ecommerceResearchPhase: "store_connect",
        ecommerceResearchProgress: { phase: "store_connect", completed: 0, total: 6 },
        ecommerceLastErrorMessage: undefined,
        ecommerceMissingDataSummary: undefined,
      });
      set((state) => ({
        coAgentType: "products" as CoAgentType,
        currentStepIndex: 0,
        stepStatuses: { ...state.stepStatuses, 0: "running" },
      }));

      _researchAbortController?.abort();
      const controller = new AbortController();
      _researchAbortController = controller;

      try {
        await runEcommerceResearchSSE(normalizedInput, sessionId, controller.signal);
        if (get().campaignData.ecommerceResearchStatus === "running") {
          updateCampaignData({ ecommerceResearchStatus: "complete" });
        }
      } catch (e: any) {
        if (e.name === "AbortError") return;
        updateCampaignData({ ecommerceResearchStatus: "error", ecommerceResearchPhase: "error" });
        addStepLog(0, `[ERROR] ${e.message}`);
        set((state) => ({
          currentStepIndex: 0,
          stepStatuses: { ...state.stepStatuses, 0: "waiting" },
        }));
      } finally {
        if (_researchAbortController === controller) {
          _researchAbortController = null;
        }
      }
    },

    startEcommerceMetrics: async () => {
      const { sessionId, addStepLog, updateCampaignData, campaignData } = get();
      if (!sessionId) return;

      const storeId = campaignData.ecommerceStoreId;
      const ecommerceInput = campaignData.ecommerceInput;
      if (!storeId || !ecommerceInput) {
        addStepLog(1, "[ERROR] Missing store ID or input data");
        return;
      }

      updateCampaignData({
        ecommerceResearchPhase: "metrics_calculation",
        ecommerceSubPhases: [
          { phase: "metrics_calculation", label: "Computing economics", status: "pending" },
          { phase: "priority_scoring", label: "Scoring priorities", status: "pending" },
        ],
      });

      _researchAbortController?.abort();
      const controller = new AbortController();
      _researchAbortController = controller;

      try {
        await runEcommerceMetricsSSE(
          {
            storeId,
            connectionMethod: ecommerceInput.connectionMethod ?? "shopify",
            businessName: ecommerceInput.businessName,
            industry: ecommerceInput.industry,
            averageOrderValue: ecommerceInput.averageOrderValue,
            monthlyRevenue: ecommerceInput.monthlyRevenue,
            totalAdSpend: ecommerceInput.totalAdSpend,
            totalCustomers: ecommerceInput.totalCustomers,
          },
          sessionId,
          controller.signal,
        );
      } catch (e: any) {
        if (e.name === "AbortError") return;
        addStepLog(1, `[ERROR] ${e.message}`);
        set((state) => ({
          currentStepIndex: 1,
          stepStatuses: { ...state.stepStatuses, 1: "waiting" },
        }));
      } finally {
        if (_researchAbortController === controller) {
          _researchAbortController = null;
        }
      }
    },

    startContentIntelligence: async (input: ContentIntelligenceInput) => {
      const { sessionId, addStepLog, addMessage, updateCampaignData } = get();
      if (!sessionId) return;

      set((state) => {
        const statuses = clearActiveStepStatuses(
          state.stepStatuses,
          getMaxStepIndexForType(state.coAgentType),
        );
        for (let i = 0; i < CONTENT_INSPIRATION_STEP_INDEX; i += 1) {
          statuses[i] = "completed";
        }
        statuses[CONTENT_INSPIRATION_STEP_INDEX] = "running";
        return {
          currentStepIndex: CONTENT_INSPIRATION_STEP_INDEX,
          focusedStepIndex: CONTENT_INSPIRATION_STEP_INDEX,
          stepStatuses: statuses,
        };
      });

      updateCampaignData({
        contentIntelligenceStatus: "running",
        contentIntelligencePhase: "keyword_generation",
      });
      addStepLog(CONTENT_INSPIRATION_STEP_INDEX, "Starting content inspiration analysis...");

      addMessage({
        role: "agent",
        content: `Starting content intelligence analysis — discovering top-performing ads, analyzing creative patterns with Gemini, and generating content briefs...`,
      });

      _researchAbortController?.abort();
      const controller = new AbortController();
      _researchAbortController = controller;

      try {
        await runContentIntelligenceSSE(input, sessionId, controller.signal);
      } catch (e: any) {
        if (e.name === "AbortError") return;
        updateCampaignData({
          contentIntelligenceStatus: "error",
          contentIntelligencePhase: "error",
        });
        addStepLog(CONTENT_INSPIRATION_STEP_INDEX, `[ERROR] ${e.message}`);
        addMessage({
          role: "agent",
          content: `Content intelligence failed: ${e.message}. Please try again.`,
        });
      } finally {
        if (_researchAbortController === controller) {
          _researchAbortController = null;
        }
      }
    },

    approveContentBrief: async (briefId: string, action: "approve" | "reject") => {
      const { addMessage, updateCampaignData } = get();
      try {
        const res = await fetch(`${env.coreUrl}/core/coagent/content-brief-status`, {
          method: "POST",
          headers: await authHeaders(),
          body: JSON.stringify({ briefId, action }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: "Update failed" }));
          throw new Error(err.error || `Update failed (${res.status})`);
        }
        const result = await res.json();
        const cd = get().campaignData;
        const approvedIds = new Set(cd.approvedBriefIds ?? []);
        if (action === "approve") {
          approvedIds.add(briefId);
        } else {
          approvedIds.delete(briefId);
        }
        updateCampaignData({ approvedBriefIds: [...approvedIds] });
        addMessage({
          role: "agent",
          content:
            action === "approve"
              ? `Brief approved for ${result.platform} (${result.productTitle ?? "product"}). Ready for production.`
              : `Brief sent back to draft for revision.`,
        });
      } catch (e: any) {
        addMessage({ role: "agent", content: `Failed to ${action} brief: ${e.message}` });
      }
    },

    updateProductMetric: (productId: string, field: string, value: number) => {
      const { campaignData } = get();
      const metrics = campaignData.ecommerceProductMetrics;
      if (!metrics) return;

      const idx = metrics.findIndex((m) => m.productId === productId);
      if (idx === -1) return;

      const pm = { ...metrics[idx], [field]: value };
      pm._manualOverrides = { ...(metrics[idx]._manualOverrides ?? {}), [field]: value };
      if (field === "cogs") pm.cogsSource = "manual";

      recalcDerivedMetrics(pm);

      const updated = [...metrics];
      updated[idx] = pm;
      get().updateCampaignData({ ecommerceProductMetrics: updated });

      const backendFields = [
        "rsp",
        "cogs",
        "deliveryCost",
        "stockLevel",
        "unitsSold",
        "leadTime",
        "cac",
        "ltv12m",
      ];
      if (backendFields.includes(field)) {
        const storeId = campaignData.ecommerceStoreId ?? "";
        authHeaders().then((headers) => {
          fetch(`${env.coreUrl}/core/coagent/product-metric-update`, {
            method: "POST",
            headers,
            body: JSON.stringify({ productId, storeId, [field]: value }),
          }).catch(() => {
            /* best-effort persist */
          });
        });
      }
    },

    bulkUpdateProductMetric: (productIds: string[], field: string, value: number) => {
      const { campaignData } = get();
      const metrics = campaignData.ecommerceProductMetrics;
      if (!metrics || productIds.length === 0) return;

      const idSet = new Set(productIds);
      const updated = metrics.map((m) => {
        if (!idSet.has(m.productId)) return m;
        const pm = { ...m, [field]: value };
        pm._manualOverrides = { ...(m._manualOverrides ?? {}), [field]: value };
        if (field === "cogs") pm.cogsSource = "manual";
        recalcDerivedMetrics(pm);
        return pm;
      });

      get().updateCampaignData({ ecommerceProductMetrics: updated });

      const backendFields = [
        "rsp",
        "cogs",
        "deliveryCost",
        "stockLevel",
        "unitsSold",
        "leadTime",
        "cac",
        "ltv12m",
      ];
      if (backendFields.includes(field)) {
        const storeId = campaignData.ecommerceStoreId ?? "";
        authHeaders().then((headers) => {
          for (const pid of productIds) {
            fetch(`${env.coreUrl}/core/coagent/product-metric-update`, {
              method: "POST",
              headers,
              body: JSON.stringify({ productId: pid, storeId, [field]: value }),
            }).catch(() => {
              /* best-effort */
            });
          }
        });
      }
    },

    startSocialVideoGeneration: async (input) => {
      const { sessionId, addMessage, updateCampaignData } = get();
      if (!sessionId) return;

      updateCampaignData({
        socialVideoGenerationState: {
          status: "running",
          phase: "starting",
          progressMessage: "Starting video generation...",
        },
      });

      const controller = new AbortController();

      try {
        await runSocialVideoGenerateSSE(input, sessionId, controller.signal);
      } catch (e: any) {
        if (e.name === "AbortError") return;
        updateCampaignData({ socialVideoGenerationState: { status: "error", error: e.message } });
        addMessage({ role: "agent", content: `Video generation failed: ${e.message}` });
      }
    },

    generateSocialVideoPreview: async (input) => {
      const { sessionId, updateCampaignData } = get();
      if (!sessionId) return;

      try {
        const res = await fetch(`${env.coreUrl}/core/coagent/social-video-preview`, {
          method: "POST",
          headers: await authHeaders(),
          body: JSON.stringify({ sessionId, ...input }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: "Preview failed" }));
          throw new Error(err.error || `Preview failed (${res.status})`);
        }
        const { previewUrl, templateId } = await res.json();
        const existing = get().campaignData.socialVideoPreviews ?? {};
        updateCampaignData({
          socialVideoPreviews: { ...existing, [templateId || input.templateId]: previewUrl },
        });
      } catch (e: any) {
        console.error("[coagent/social-video-preview] failed:", e.message);
      }
    },

    sendBriefToProduction: async (input) => {
      const { sessionId, addMessage, updateCampaignData } = get();
      if (!sessionId) return;

      try {
        const res = await fetch(`${env.coreUrl}/core/coagent/social-production-handoff`, {
          method: "POST",
          headers: await authHeaders(),
          body: JSON.stringify({ sessionId, ...input }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: "Handoff failed" }));
          throw new Error(err.error || `Handoff failed (${res.status})`);
        }
        const { handoff } = await res.json();
        const existing = get().campaignData.socialProductionHandoffs ?? [];
        updateCampaignData({ socialProductionHandoffs: [...existing, handoff] });
        addMessage({
          role: "agent",
          content: `Brief sent to ${handoff.pipeline === "cinematic" ? "Cinematic Engine" : "UGC Creator"}! Session created. View production queue below.`,
          widget: "videoProductionQueue",
        });
      } catch (e: any) {
        addMessage({
          role: "agent",
          content: `Failed to send brief to production: ${e.message}`,
        });
      }
    },
  };
}
